/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { useAgencyPermissions } from '@/features/agency';
import { useLeaseSettings } from '@features/leases/hooks/useLeaseSettings';
import { leasesKeys } from '@features/leases/hooks/leases-keys';
import type { LeaseDocumentCategory } from '@features/leases/types/lease.types';
import { AdaptiveFormShell } from '@/shared/ui/components/form';
import { NativeRadio, Text, TextInput, UnstyledButton } from '@/shared/ui';
import { notifications } from '@/shared/ui/components/basic/Notifications';

import {
  errorClasses, fieldsetClasses, labelClasses, submitBtnClasses,
} from './dialog-styles';
import { fieldLabelStyle } from './settings-block-styles';
import { blockRowStyle } from '@/shared/ui/components/composed/settings-block/settings-block.styles';
import { BlockMid, BlockSwitch, SettingsBlock } from './blocks';
import { BlockError, BlockLoading } from './BlockState';
import { DocumentCategoryTiles } from './DocumentCategoryTiles';
import {
  SettingsItemList, summaryOf, type ItemSectionProps, type ItemSummaries,
} from './SettingsItemList';
import { useRegisterSettingsDirty } from '../hooks/use-settings-dirty';
import { itemsOf, type SettingsItemKey } from '../constants/settings-items';

/** The five ids the backend seeds, which i18n still translates by id. */
const BUILT_IN_CATEGORY_IDS = new Set([
  'contract', 'inspection', 'warranty', 'termination', 'other',
]);

/** How a due date that lands on a weekend or holiday moves. */
const BUSINESS_DAY_RULES = ['postpone', 'anticipate'] as const;

/** The day a lease is born with when the agency never chose one. */
const FALLBACK_PAYMENT_DAY = 5;

const LEASE_BLOCKS: Partial<Record<SettingsItemKey, ComponentType>> = {
  leaseDueDate: DueDateBlock,
  leaseCategories: DocumentCategoriesBlock,
};

/**
 * Everything about Locações an agency configures: the due date a new lease
 * starts from, and what its documents are filed under.
 *
 * Opens on a list with each setting's current value; a row opens that
 * setting alone.
 *
 * @returns The lease settings section.
 */
export function LeaseSettingsSection({ item = null, onOpenItem = () => undefined }: ItemSectionProps) {
  const summaries = useLeaseSummaries();
  const Block = item ? LEASE_BLOCKS[item] : undefined;
  if (Block) return <Block />;
  return <SettingsItemList items={itemsOf('locacoes')} summaries={summaries} onOpen={onOpenItem} />;
}

/**
 * Each lease setting's current value, in the words the list shows.
 *
 * @returns The summaries keyed by item.
 */
function useLeaseSummaries(): ItemSummaries {
  const { t } = useTranslation('features/agency-settings');
  const { agency } = useAgency((state) => state);
  const lease = useLeaseSettings(agency.id ?? '');

  return {
    leaseDueDate: summaryOf(lease, () => t('v2.items.leaseDueDate.summary', {
      day: lease.defaultPaymentDay ?? FALLBACK_PAYMENT_DAY,
      rule: t(`v2.items.leaseDueDate.rules.${lease.businessDayRule ?? 'postpone'}`),
    })),
    leaseCategories: summaryOf(lease, () => t('v2.items.categoryCount', { count: lease.documentCategories.length })),
  };
}

/** The result of parsing and validating the due-day draft. */
type DayParse = { ok: true; day: number | null } | { ok: false };

/**
 * Whether *day* is a valid day of the month (1–31).
 *
 * @param day - The parsed day.
 * @returns True when it is a whole day within range.
 */
function isValidDueDay(day: number): boolean {
  return Number.isInteger(day) && day >= 1 && day <= 31;
}

/**
 * Parses the due-day draft: blank means "unset" (null), otherwise a day of
 * the month.
 *
 * @param draft - The raw text from the day field.
 * @returns The parsed day (or null), or a failure when out of range.
 */
function parseDueDay(draft: string): DayParse {
  const trimmed = draft.trim();
  if (trimmed === '') return { ok: true, day: null };
  const day = Number(trimmed);
  return isValidDueDay(day) ? { ok: true, day } : { ok: false };
}

/**
 * The due date a new lease starts from: the day, what happens when it lands
 * on a weekend or holiday, and whether the discount deadline moves with it.
 *
 * Each rule shows an example date instead of leaving "postpone" and
 * "anticipate" to be decoded, and the switch says what it does in both
 * positions.
 *
 * @returns The due-date block.
 */
function DueDateBlock() {
  const { t } = useTranslation('features/agency-settings');
  const { agency } = useAgency((state) => state);
  const queryClient = useQueryClient();
  const { canManageLeaseSettings: canManage } = useAgencyPermissions();
  const {
    defaultPaymentDay, businessDayRule, shiftDiscountDeadline,
    saveDueDateDefaults, isLoading, isError,
  } = useLeaseSettings(agency.id ?? '');
  const [dayDraft, setDayDraft] = useState('');
  const [ruleDraft, setRuleDraft] = useState('');
  const [shiftDraft, setShiftDraft] = useState(true);
  const [saving, setSaving] = useState(false);

  // Mirrors what is stored until the user types.
  useEffect(() => {
    setDayDraft(defaultPaymentDay === null ? '' : String(defaultPaymentDay));
    setRuleDraft(businessDayRule ?? 'postpone');
    setShiftDraft(shiftDiscountDeadline ?? true);
  }, [defaultPaymentDay, businessDayRule, shiftDiscountDeadline]);

  const persistDueDate = async (day: number | null) => {
    setSaving(true);
    try {
      await saveDueDateDefaults({
        default_payment_day: day,
        business_day_rule: ruleDraft,
        shift_discount_deadline: shiftDraft,
      });
      notifications.show({ color: 'green', message: t('v2.locacoes.dueDate.saved') });
    } catch {
      notifications.show({ color: 'red', message: t('v2.locacoes.dueDate.saveError') });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const parsed = parseDueDay(dayDraft);
    if (!parsed.ok) {
      notifications.show({ color: 'red', message: t('v2.locacoes.dueDate.invalidDay') });
      return;
    }
    await persistDueDate(parsed.day);
  };

  const storedDay = defaultPaymentDay === null ? '' : String(defaultPaymentDay);
  const storedRule = businessDayRule ?? 'postpone';
  const storedShift = shiftDiscountDeadline ?? true;

  const resetDrafts = () => {
    setDayDraft(storedDay);
    setRuleDraft(storedRule);
    setShiftDraft(storedShift);
  };

  useRegisterSettingsDirty({
    isDirty: canManage
      && (dayDraft !== storedDay || ruleDraft !== storedRule || shiftDraft !== storedShift),
    isSaving: saving,
    onSave: () => void handleSave(),
    onDiscard: resetDrafts,
  });

  const disabled = !canManage || saving;

  const renderBody = () => {
    if (isLoading) return <BlockLoading rows={4} />;
    if (isError) {
      return <BlockError onRetry={() => void queryClient.refetchQueries({ queryKey: leasesKeys.settings(agency.id ?? '') })} />;
    }
    return (
      <>
        <BlockMid>
          <div className="flex flex-col gap-(--space-1-5)">
            <Text as="label" htmlFor="lease-default-payment-day" className={labelClasses}>
              {t('v2.locacoes.dueDate.dayField')}
            </Text>
            <div className="flex items-center gap-(--space-2)" style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-primary)' }}>
              <Text component="span">{t('v2.locacoes.dueDate.dayPrefix')}</Text>
              <div style={{ width: 80 }}>
                <TextInput
                  id="lease-default-payment-day"
                  name="lease-default-payment-day"
                  value={dayDraft}
                  placeholder={t('v2.locacoes.dueDate.dayPlaceholder')}
                  disabled={disabled}
                  onChange={(e) => setDayDraft(e.target.value)}
                  aria-describedby="lease-default-payment-day-help"
                  data-testid="lease-payment-day-input"
                />
              </div>
              <Text component="span">{t('v2.locacoes.dueDate.daySuffix')}</Text>
            </div>
            <Text component="span" id="lease-default-payment-day-help" style={{ ...fieldLabelStyle, fontWeight: 400 }}>
              {t('v2.locacoes.dueDate.dayNote')}
            </Text>
          </div>
        </BlockMid>

        <fieldset style={{ margin: 0, padding: 0, border: 0, borderTop: '1px solid var(--border-ultra-subtle)' }}>
          <Text
            component="legend"
            className={labelClasses}
            style={{ padding: 'var(--space-3) var(--space-5) var(--space-1)' }}
          >
            {t('v2.locacoes.dueDate.ruleField')}
          </Text>
          {BUSINESS_DAY_RULES.map((rule) => (
            <Text
              as="label"
              key={rule}
              htmlFor={`lease-rule-${rule}`}
              className="cursor-pointer hover:bg-(--surface-hover)"
              style={{ ...blockRowStyle, alignItems: 'flex-start' }}
            >
              <NativeRadio
                id={`lease-rule-${rule}`}
                name="lease-business-day-rule"
                value={rule}
                checked={ruleDraft === rule}
                disabled={disabled}
                onChange={() => setRuleDraft(rule)}
                style={{ width: 18, height: 18, marginTop: 2, accentColor: 'var(--seed-base)', flex: 'none' }}
                data-testid={`lease-rule-${rule}`}
              />
              <Text component="span" style={{ minWidth: 0 }}>
                <Text component="span" style={{ display: 'block', fontSize: 'var(--text-body-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {t(`v2.locacoes.dueDate.rules.${rule}`)}
                </Text>
                <Text component="span" style={{ ...fieldLabelStyle, display: 'block', marginTop: 2, fontWeight: 400 }}>
                  {t(`v2.locacoes.dueDate.ruleExamples.${rule}`)}
                </Text>
              </Text>
            </Text>
          ))}
        </fieldset>

        <div style={{ ...blockRowStyle, justifyContent: 'space-between', gap: 'var(--space-4)', borderBottom: 'none' }}>
          <div style={{ minWidth: 0 }}>
            <Text style={{ margin: 0, fontSize: 'var(--text-body-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>
              {t('v2.locacoes.dueDate.shiftField')}
            </Text>
            <Text style={{ ...fieldLabelStyle, marginTop: 'var(--space-0-5)', fontWeight: 400 }}>
              {shiftDraft ? t('v2.locacoes.dueDate.shiftOn') : t('v2.locacoes.dueDate.shiftOff')}
            </Text>
          </div>
          <BlockSwitch
            checked={shiftDraft}
            onChange={setShiftDraft}
            disabled={disabled}
            ariaLabel={t('v2.locacoes.dueDate.shiftField')}
            testId="lease-shift-discount-deadline"
          />
        </div>
      </>
    );
  };

  return (
    <SettingsBlock
      title={t('v2.locacoes.dueDate.title')}
      caption={t('v2.locacoes.dueDate.explanation')}
      testId="lease-due-date-block"
    >
      {renderBody()}
    </SettingsBlock>
  );
}

/**
 * The vocabulary a lease's documents are filed under. Adding and renaming
 * only, for the same reason as the sale categories.
 *
 * @returns The document-categories block.
 */
function DocumentCategoriesBlock() {
  const { t } = useTranslation('features/agency-settings');
  const { t: tLeases } = useTranslation('features/leases');
  const { agency } = useAgency((state) => state);
  const queryClient = useQueryClient();
  const { canManageLeaseSettings: canManage } = useAgencyPermissions();
  const {
    documentCategories, addDocumentCategory, renameDocumentCategory, isLoading, isError,
  } = useLeaseSettings(agency.id ?? '');
  const [editing, setEditing] = useState<LeaseDocumentCategory | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  /** Built-in ids stay translated; an agency's own category shows its label. */
  const categoryLabel = (category: LeaseDocumentCategory) =>
    (BUILT_IN_CATEGORY_IDS.has(category.id)
      ? tLeases(`documents.categories.${category.id}`, { defaultValue: category.label })
      : category.label);

  const renderBody = () => {
    if (isLoading) return <BlockLoading rows={3} />;
    if (isError) {
      return <BlockError onRetry={() => void queryClient.refetchQueries({ queryKey: leasesKeys.settings(agency.id ?? '') })} />;
    }
    return (
      <BlockMid>
        <DocumentCategoryTiles
          categories={documentCategories}
          labelOf={categoryLabel}
          canManage={canManage}
          onRename={setEditing}
          emptyText={t('v2.locacoes.categories.empty')}
          testId="lease-document-categories"
        />
      </BlockMid>
    );
  };

  return (
    <SettingsBlock
      title={t('v2.locacoes.categories.title')}
      caption={t('v2.locacoes.categories.explanation')}
      action={canManage && !isLoading
        ? { label: t('v2.locacoes.categories.add'), onClick: () => setIsAdding(true), testId: 'lease-category-add' }
        : undefined}
      note={t('v2.locacoes.categories.note')}
      testId="lease-categories-block"
    >
      {renderBody()}

      {isAdding && (
        <CategoryModal
          category={null}
          onClose={() => setIsAdding(false)}
          onSubmit={async (label) => {
            await addDocumentCategory(label);
            setIsAdding(false);
          }}
        />
      )}

      {editing && (
        <CategoryModal
          category={editing}
          initialLabel={categoryLabel(editing)}
          onClose={() => setEditing(null)}
          onSubmit={async (label) => {
            await renameDocumentCategory(editing.id, label);
            setEditing(null);
          }}
        />
      )}
    </SettingsBlock>
  );
}

interface CategoryModalProps {
  /** Null when adding; the category being renamed otherwise. */
  category: LeaseDocumentCategory | null;
  initialLabel?: string;
  onClose: () => void;
  onSubmit: (label: string) => Promise<void>;
}

/**
 * Add/rename modal for one document category, in the shape the team dialogs
 * use — the raw Modal wraps its body in a scroller, which gives a one-field
 * form a scrollbar it has no need for.
 *
 * @returns The modal element.
 */
function CategoryModal({ category, initialLabel, onClose, onSubmit }: CategoryModalProps) {
  const { t } = useTranslation('features/agency-settings');
  const [label, setLabel] = useState(initialLabel ?? '');
  const [labelError, setLabelError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!label.trim()) {
      setLabelError(t('v2.locacoes.categories.labelRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(label.trim());
      notifications.show({ color: 'green', message: t('v2.locacoes.categories.saved') });
    } catch {
      notifications.show({ color: 'red', message: t('v2.locacoes.categories.saveError') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdaptiveFormShell
      open
      onClose={onClose}
      title={category ? t('v2.locacoes.categories.editTitle') : t('v2.locacoes.categories.addTitle')}
      density="compact"
      footer={
        <UnstyledButton
          onClick={() => void handleSubmit()}
          disabled={submitting}
          className={submitBtnClasses}
          data-testid="lease-category-modal-submit"
        >
          {submitting ? t('v2.locacoes.categories.saving') : t('v2.locacoes.categories.save')}
        </UnstyledButton>
      }
    >
      <div data-testid="lease-category-form" className="flex flex-col gap-(--space-4)">
        <fieldset className={fieldsetClasses}>
          <Text as="label" htmlFor="lease-category-label" className={labelClasses}>
            {t('v2.locacoes.categories.labelField')}
          </Text>
          <TextInput
            id="lease-category-label"
            name="lease-category-label"
            value={label}
            placeholder={t('v2.locacoes.categories.labelPlaceholder')}
            onChange={(e) => { setLabel(e.target.value); setLabelError(''); }}
            autoFocus
          />
          {labelError && <Text component="span" className={errorClasses}>{labelError}</Text>}
        </fieldset>
      </div>
    </AdaptiveFormShell>
  );
}
